import { Link } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { CustomerProduct, DurationType } from "../../types";
import { daysUntil, formatDate, formatDuration, formatMoney } from "../../utils/format";
import DataTable, { Column, RowActions } from "../../components/ui/DataTable";
import Badge, { StatusBadge } from "../../components/ui/Badge";
import { IconButton } from "../../components/ui/Button";

/** "Overdue 3d" / "in 12d" / "in 200d" pill for renewal dates */
export function RenewalChip({ date, status }: { date: string | null; status?: string }) {
    const days = daysUntil(date);
    if (days === null) return null;
    if (status === "CANCELLED") return null;
    if (days < 0) return <Badge tone="red">Overdue {Math.abs(days)}d</Badge>;
    if (days === 0) return <Badge tone="red">Today</Badge>;
    if (days <= 30) return <Badge tone="amber">in {days}d</Badge>;
    return <span className="text-[11px] text-slate-500">in {days}d</span>;
}

/** "1 Year" stays "1 Year"; "Gold" becomes "Gold · 1 year" */
const planLabel = (p: { name: string; duration: number; durationType: DurationType }) => {
    const length = formatDuration(p.duration, p.durationType);
    return p.name.toLowerCase().replace(/s$/, "") === length.replace(/s$/, "") ? p.name : `${p.name} · ${length}`;
};

interface Props {
    rows: CustomerProduct[] | null;
    loading?: boolean;
    error?: string;
    onRetry?: () => void;
    showCustomer?: boolean;
    onEdit?: (row: CustomerProduct) => void;
    onDelete?: (row: CustomerProduct) => void;
    emptyTitle?: string;
    emptyText?: string;
    emptyAction?: React.ReactNode;
}

export default function CustomerProductTable({
    rows,
    loading,
    error,
    onRetry,
    showCustomer = true,
    onEdit,
    onDelete,
    emptyTitle = "No products assigned",
    emptyText,
    emptyAction,
}: Props) {
    const columns: Column<CustomerProduct>[] = [];

    if (showCustomer) {
        columns.push({
            key: "customer",
            header: "Customer",
            render: (r) => (
                <Link to={`/customers/${r.customer.id}`} className="group block min-w-0">
                    <p className="font-semibold text-white group-hover:text-[#a78bfa] transition-colors truncate">{r.customer.name}</p>
                    <p className="text-xs font-mono text-slate-500">#{r.customer.customerId}</p>
                </Link>
            ),
        });
    }

    columns.push(
        {
            key: "product",
            header: "Product",
            render: (r) => (
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-100 truncate">{r.product.name}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <StatusBadge value={r.product.type} />
                        <span className="text-xs text-slate-500 truncate">{r.product.category?.name}</span>
                    </div>
                </div>
            ),
        },
        {
            key: "details",
            header: "Details",
            hideOnMobile: true,
            render: (r) =>
                r.product.type === "HARDWARE" ? (
                    <div className="space-y-1">
                        <StatusBadge value={r.deliveryStatus} />
                        <p className="text-xs text-slate-500 whitespace-nowrap">{r.deliveryDate ? `Delivery ${formatDate(r.deliveryDate)}` : "No delivery date"}</p>
                    </div>
                ) : (
                    <div className="text-xs space-y-0.5">
                        <p className="text-slate-300">
                            {r.subscriptionPlan ? planLabel(r.subscriptionPlan) : "—"}
                        </p>
                        <p className="text-slate-500">
                            {r.deviceType?.name ?? "Any device"}
                            {r.product.zatcaPhase ? ` · ZATCA ${r.product.zatcaPhase === "PHASE_1" ? "1" : "2"}` : ""}
                        </p>
                    </div>
                ),
        },
        {
            key: "amount",
            header: "Amount",
            render: (r) => (
                <div className="text-xs whitespace-nowrap">
                    <p className="text-sm font-semibold text-white">{formatMoney(r.amount)}</p>
                    {r.renewalAmount && <p className="text-slate-500">Renews {formatMoney(r.renewalAmount)}</p>}
                </div>
            ),
        },
        {
            key: "renewal",
            header: "Renewal",
            hideOnMobile: true,
            render: (r) =>
                r.product.type === "SOFTWARE" ? (
                    <div className="space-y-1 whitespace-nowrap">
                        <p className="text-slate-300 text-xs">{formatDate(r.renewalDate)}</p>
                        <RenewalChip date={r.renewalDate} status={r.status} />
                    </div>
                ) : (
                    <span className="text-slate-600">—</span>
                ),
        },
        {
            key: "status",
            header: "Status",
            render: (r) => (
                <div className="flex flex-col items-start gap-1">
                    {r.product.type === "SOFTWARE" && <StatusBadge value={r.status} />}
                    <StatusBadge value={r.paymentStatus} />
                </div>
            ),
        }
    );

    if (onEdit || onDelete) {
        columns.push({
            key: "actions",
            header: "",
            className: "w-24 text-right",
            render: (r) => (
                <RowActions>
                    {onEdit && <IconButton icon={Pencil} label="Edit" onClick={() => onEdit(r)} />}
                    {onDelete && <IconButton icon={Trash2} label="Delete" tone="danger" onClick={() => onDelete(r)} />}
                </RowActions>
            ),
        });
    }

    return (
        <DataTable
            columns={columns}
            rows={rows}
            loading={loading}
            error={error}
            onRetry={onRetry}
            rowKey={(r) => r.id}
            emptyTitle={emptyTitle}
            emptyText={emptyText}
            emptyAction={emptyAction}
        />
    );
}